/**
 * Canvas spinning wheel. Purely cosmetic: the winner is decided before `spin`
 * is called and the wheel is rotated to land on that winner's segment.
 *
 * Large pools are shown as a random sample of SEGMENT_LIMIT entries that
 * always includes the winner — the sample never changes anyone's odds, which
 * were fixed when the winner was drawn from the full eligible pool.
 */
import { sample, secureRandom, shuffle } from '../lib/random.js';
import { fmt, themeVar } from '../ui/dom.js';
import { animate, easeOutCubic } from './easing.js';

export const SEGMENT_LIMIT = 40;
const TAU = Math.PI * 2;
const POINTER_ANGLE = -Math.PI / 2; // 12 o'clock
const FONT_STACK =
  "'Inter Variable', 'Noto Sans Myanmar', system-ui, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji'";

const mod = (n, m) => ((n % m) + m) % m;

function textColorFor(hex) {
  const m = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex.trim());
  if (!m) return '#ffffff';
  const [r, g, b] = m.slice(1).map((h) => parseInt(h, 16) / 255);
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum > 0.55 ? '#0b0d12' : '#ffffff';
}

const graphemes = (text) =>
  typeof Intl.Segmenter === 'function'
    ? [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(text)].map((s) => s.segment)
    : [...text];

function fitText(ctx, text, maxWidth) {
  if (ctx.measureText(text).width <= maxWidth) return text;
  const parts = graphemes(text);
  while (parts.length > 1 && ctx.measureText(`${parts.join('')}…`).width > maxWidth) parts.pop();
  return `${parts.join('')}…`;
}

export function createWheelView({ canvas, pointer, note, onFrame = () => {} }) {
  const ctx = canvas.getContext('2d');
  let segments = [];
  let rotation = 0;
  let highlight = -1;
  let palette = [];
  let accent = '#ffffff';
  let pixelSize = 0;
  let lastFlick = 0;

  function refreshTheme() {
    palette = themeVar('--tk-wheel', { list: true });
    if (!palette.length) palette = ['#7c9cff', '#2a3150'];
    accent = themeVar('--tk-accent') || '#ffffff';
  }

  function resize() {
    const cssSize = Math.round(canvas.getBoundingClientRect().width);
    if (!cssSize) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    pixelSize = Math.round(cssSize * dpr);
    canvas.width = pixelSize;
    canvas.height = pixelSize;
    draw();
  }

  function colorFor(i, n) {
    let color = palette[i % palette.length];
    // Avoid two neighbours sharing a colour where the circle closes.
    if (i === n - 1 && n > 1 && color === palette[0]) color = palette[(i + 1) % palette.length];
    return color;
  }

  function draw() {
    if (!pixelSize) return;
    const S = pixelSize;
    const cx = S / 2;
    const cy = S / 2;
    const R = S / 2 - S * 0.03;
    ctx.clearRect(0, 0, S, S);

    // Rim
    ctx.beginPath();
    ctx.arc(cx, cy, R + S * 0.022, 0, TAU);
    ctx.fillStyle = accent;
    ctx.fill();

    const n = segments.length;
    if (!n) {
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, TAU);
      ctx.fillStyle = palette[1] ?? '#222';
      ctx.fill();
      return;
    }

    const arc = TAU / n;
    const fontPx = Math.max(S * 0.022, Math.min(R * 0.075, R * 0.6 * arc * 0.55));
    ctx.font = `600 ${fontPx}px ${FONT_STACK}`;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';

    for (let i = 0; i < n; i++) {
      const start = rotation + i * arc;
      const color = colorFor(i, n);
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, R, start, start + arc);
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();
      if (n > 1) {
        ctx.strokeStyle = 'rgba(0,0,0,0.22)';
        ctx.lineWidth = Math.max(1, S * 0.002);
        ctx.stroke();
      }
      if (i === highlight) {
        ctx.fillStyle = 'rgba(255,255,255,0.28)';
        ctx.fill();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = S * 0.008;
        ctx.stroke();
      }

      const entry = segments[i];
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(start + arc / 2);
      ctx.fillStyle = textColorFor(color);
      ctx.fillText(fitText(ctx, entry.displayName || entry.username, R * 0.6), R - S * 0.035, 0);
      ctx.restore();
    }

    // Pegs
    if (n <= 60) {
      for (let i = 0; i < n; i++) {
        const angle = rotation + i * arc;
        ctx.beginPath();
        ctx.arc(cx + Math.cos(angle) * (R + S * 0.011), cy + Math.sin(angle) * (R + S * 0.011), S * 0.007, 0, TAU);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
      }
    }

    // Inner shading for depth
    const shade = ctx.createRadialGradient(cx, cy, R * 0.2, cx, cy, R);
    shade.addColorStop(0, 'rgba(0,0,0,0)');
    shade.addColorStop(1, 'rgba(0,0,0,0.25)');
    ctx.beginPath();
    ctx.arc(cx, cy, R, 0, TAU);
    ctx.fillStyle = shade;
    ctx.fill();
  }

  function segmentUnderPointer() {
    const arc = TAU / segments.length;
    return Math.floor(mod(POINTER_ANGLE - rotation, TAU) / arc) % segments.length;
  }

  function flickPointer() {
    const now = performance.now();
    if (now - lastFlick < 60) return;
    lastFlick = now;
    pointer.classList.remove('flick');
    void pointer.offsetWidth;
    pointer.classList.add('flick');
  }

  function describe(total) {
    if (!total) note.textContent = 'No eligible entries yet.';
    else if (total > segments.length)
      note.textContent = `Showing ${fmt(segments.length)} of ${fmt(total)} eligible entries — every entry has an equal chance.`;
    else note.textContent = `${fmt(total)} ${total === 1 ? 'entry' : 'entries'} on the wheel.`;
  }

  refreshTheme();
  new ResizeObserver(resize).observe(canvas);

  return {
    refreshTheme() {
      refreshTheme();
      draw();
    },

    /** Idle preview of the current pool. */
    setPool(pool) {
      segments = pool.length > SEGMENT_LIMIT ? sample(pool, SEGMENT_LIMIT) : shuffle(pool);
      highlight = -1;
      describe(pool.length);
      draw();
      onFrame({ mode: 'wheel' });
    },

    async spin({ pool, winner, duration, onTick }) {
      const others = pool.filter((entry) => entry !== winner);
      segments = shuffle([...(others.length >= SEGMENT_LIMIT ? sample(others, SEGMENT_LIMIT - 1) : others), winner]);
      highlight = -1;
      describe(pool.length);

      const n = segments.length;
      const arc = TAU / n;
      const winnerIndex = segments.indexOf(winner);
      const landing = (winnerIndex + 0.15 + secureRandom() * 0.7) * arc; // somewhere inside the segment
      const start = rotation;
      const target = mod(POINTER_ANGLE - landing, TAU);
      // ~0.7 turns/second on average (peaks ~2.4 turns/s with the cubic ease).
      const turns = Math.max(2, Math.round((duration / 1000) * 0.7));
      const total = mod(target - mod(start, TAU), TAU) + turns * TAU;

      let lastIndex = segmentUnderPointer();
      await animate(duration, (t) => {
        rotation = start + total * easeOutCubic(t);
        draw();
        onFrame({ mode: 'wheel' }); // the recap video copies this canvas
        const index = segmentUnderPointer();
        if (index !== lastIndex) {
          lastIndex = index;
          onTick?.();
          flickPointer();
        }
      });

      rotation = mod(start + total, TAU);
      highlight = winnerIndex;
      draw();
      onFrame({ mode: 'wheel' });
    },
  };
}
